/**
 * Global AI Task Store - Zustand store for managing AI tasks across pages.
 * 
 * Tasks survive navigation — even if user switches tabs, the AI request
 * continues in the background and results are preserved.
 */

import { create } from 'zustand';

export interface AITask {
  id: string;
  status: 'pending' | 'running' | 'streaming' | 'completed' | 'failed';
  title: string;
  source?: string;
  provider?: string;
  providerUsed?: string;
  modelUsed?: string;
  content: string;
  error?: string;
  tokensUsed?: number;
  latencyMs?: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  // Local-only fields
  isNew?: boolean; // For notification badge
}

interface AITaskStore {
  tasks: Map<string, AITask>;
  activeTaskIds: Set<string>;
  unreadCount: number;
  panelOpen: boolean;

  // Actions
  addTask: (task: AITask) => void;
  updateTask: (id: string, updates: Partial<AITask>) => void;
  appendContent: (id: string, chunk: string) => void;
  removeTask: (id: string) => void;
  markAllRead: () => void;
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
  getTask: (id: string) => AITask | undefined;
  getTaskList: () => AITask[];
  getActiveTasks: () => AITask[];
}

export const useAITaskStore = create<AITaskStore>((set, get) => ({
  tasks: new Map(),
  activeTaskIds: new Set(),
  unreadCount: 0,
  panelOpen: false,

  addTask: (task) => set((state) => {
    const tasks = new Map(state.tasks);
    tasks.set(task.id, { ...task, isNew: true });
    const activeTaskIds = new Set(state.activeTaskIds);
    if (task.status !== 'completed' && task.status !== 'failed') {
      activeTaskIds.add(task.id);
    }
    return { tasks, activeTaskIds, unreadCount: state.unreadCount + 1 };
  }),

  updateTask: (id, updates) => set((state) => {
    const tasks = new Map(state.tasks);
    const existing = tasks.get(id);
    if (!existing) return state;
    
    const updated = { ...existing, ...updates };
    tasks.set(id, updated);
    
    const activeTaskIds = new Set(state.activeTaskIds);
    if (updated.status === 'completed' || updated.status === 'failed') {
      activeTaskIds.delete(id);
    }
    
    let unreadCount = state.unreadCount;
    if ((updates.status === 'completed' || updates.status === 'failed') && !state.panelOpen) {
      unreadCount += 1;
      updated.isNew = true;
    }
    
    return { tasks, activeTaskIds, unreadCount };
  }),

  appendContent: (id, chunk) => set((state) => {
    const tasks = new Map(state.tasks);
    const existing = tasks.get(id);
    if (!existing) return state;
    tasks.set(id, { ...existing, content: existing.content + chunk, status: 'streaming' });
    return { tasks };
  }),

  removeTask: (id) => set((state) => {
    const tasks = new Map(state.tasks);
    tasks.delete(id);
    const activeTaskIds = new Set(state.activeTaskIds);
    activeTaskIds.delete(id);
    return { tasks, activeTaskIds };
  }),

  markAllRead: () => set({ unreadCount: 0 }),

  setPanelOpen: (open) => set((state) => ({
    panelOpen: open,
    unreadCount: open ? 0 : state.unreadCount,
  })),

  togglePanel: () => set((state) => ({
    panelOpen: !state.panelOpen,
    unreadCount: !state.panelOpen ? 0 : state.unreadCount,
  })),

  getTask: (id) => get().tasks.get(id),

  getTaskList: () => {
    const tasks = Array.from(get().tasks.values());
    return tasks.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  },

  getActiveTasks: () => {
    const { tasks, activeTaskIds } = get();
    return Array.from(activeTaskIds)
      .map(id => tasks.get(id))
      .filter(Boolean) as AITask[];
  },
}));
